import { DownloadOutlined } from "@ant-design/icons";
import { Button, message } from "antd";
import { FILE_BASE_URL } from "../../../../../config";
import { handleDownload } from "../../../../../utils/files";
import { useCallback, useMemo, useState } from "react";
import PdfViewer from "../../../../../components/shared/PdfViewer";
import HtmlViewer from "../../../../../components/shared/HtmlViewer";
import PrintableImage from "../../../../../components/shared/PrintableImage";
import { useDispatch } from "react-redux";
import { updateFileKey } from "../../../../../store/chats/thunks";

const DocItem = ({ file, selectedTenantId, selectedChatId }) => {
  const [mimeType, setMimeType] = useState(null);

  const fileUrl = useMemo(
    () => `${FILE_BASE_URL}${file?.storage_url}`,
    [file?.storage_url],
  );
  const dispatch = useDispatch();

  const onError = useCallback(async () => {
    return await dispatch(
      updateFileKey({
        attachment_id: file?.id,
        doc_id: file?.id,
        tenant_id: selectedTenantId,
        conversation_id: selectedChatId,
      }),
    );
  }, [message, selectedTenantId, selectedChatId, file]);

  const downloadWithFallback = useCallback(async () => {
    try {
      // First attempt
      const failed = await handleDownload(fileUrl, file?.filename);

      if (!failed) return;

      const { payload } = await onError();

      if (!payload?.url) throw new Error("URL Not Found");

      const retryFailed = await handleDownload(
        `${FILE_BASE_URL}${payload.url}`,
        file?.filename,
      );

      if (retryFailed) throw new Error("URL Not Found");
    } catch (error) {
      message.error("Download failed. Please try again after some time.");
    }
  }, [fileUrl, file, onError]);

  return (
    <>
      <div
        key={file.id}
        className="file-item cursor-pointer"
        onClick={() => {
          const mime = file?.mime_type || "";
          if (mime.includes("pdf")) {
            setMimeType("pdf");
          } else if (mime.includes("html")) {
            setMimeType("html");
          } else if (!file?.mime_type?.startsWith("image")) {
            message.info(`No viewer available for file : ${file?.filename}`);
          }
        }}
      >
        <div className="file-icon-container">
          {file?.mime_type?.startsWith("image") ? (
            <>
              <PrintableImage
                src={fileUrl}
                alt={file.filename}
                className="image-file"
                onError={onError}
                isDoc
                key={file?.id}
              />
            </>
          ) : (
            <>
              <div className="file-icon">📄</div>
              <span className={`type-badge ${file.type}`}>
                {file?.type?.toUpperCase()}
              </span>
            </>
          )}
        </div>

        <div className="file-content">
          <p className="file-name">{file.filename}</p>
          {file?.content && <p className="file-caption">{file?.content}</p>}
          <div className="file-meta">{file?.meta}</div>
        </div>

        <Button
          className="download-button"
          onClick={(e) => {
            e.stopPropagation();
            downloadWithFallback();
          }}
          icon={<DownloadOutlined />}
          shape="circle"
        />
      </div>
      {mimeType === "pdf" ? (
        <PdfViewer
          key={file?.id}
          pdfUrl={fileUrl}
          onClose={() => setMimeType(null)}
          onError={onError}
        />
      ) : mimeType === "html" ? (
        <HtmlViewer
          key={file?.id}
          url={fileUrl}
          onClose={() => setMimeType(null)}
          onError={onError}
        />
      ) : (
        <></>
      )}
    </>
  );
};

export default DocItem;
